import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchTicketCustomerHistory,
  fetchTicketHistorySummaryStatus,
  triggerTicketHistorySummary,
} from "@/pages/tickets/api/ticketHistoryApi";

export function useTicketCustomerHistory(ticketId, workspaceId) {
  const [historicThreads, setHistoricThreads] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [summaryJob, setSummaryJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!ticketId) {
      setHistoricThreads([]);
      setAiSummary(null);
      setSummaryJob(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchTicketCustomerHistory({ ticketId, workspaceId });
      const groups = Array.isArray(raw?.related_ticket_groups) ? raw.related_ticket_groups : [];
      const mappedThreads = groups.map((g) => ({
        id: String(g.ticket_id ?? ""),
        navigable: true,
        startedAtLabel: String(g.started_at_label ?? "—"),
        preview: String(g.preview ?? ""),
        status: String(g.status ?? "open"),
        messageCount: typeof g.user_message_count === "number" ? g.user_message_count : 0,
        matchReasons: ["email"],
      }));
      setHistoricThreads(mappedThreads);
      setAiSummary(raw?.summary ?? null);
      setSummaryJob(raw?.summary_job ?? null);

      const status = String(raw?.summary_job?.status ?? "");
      const isFresh = Boolean(raw?.summary_job?.is_fresh);
      if (!isFresh && (status === "not_requested" || status === "failed")) {
        // The AI summary is best-effort (needs a tenant OpenAI key and can 500
        // server-side). Its failure must NOT blank the historic-tickets list —
        // isolate it so the successfully-fetched history still renders.
        try {
          const trig = await triggerTicketHistorySummary({ ticketId, workspaceId, forceRefresh: false });
          setSummaryJob((prev) => ({ ...(prev || {}), ...(trig || {}), status: trig?.status || "queued" }));
        } catch {
          /* ignore — summary is optional; history is already loaded */
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setHistoricThreads([]);
      setAiSummary(null);
      setSummaryJob(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId, workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!ticketId || !summaryJob) return undefined;
    const s = String(summaryJob.status || "");
    if (s !== "queued" && s !== "running") return undefined;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const st = await fetchTicketHistorySummaryStatus({ ticketId, workspaceId });
        if (cancelled) return;
        setSummaryJob(st);
        if (st?.summary) setAiSummary(st.summary);
        const next = String(st?.status || "");
        if ((next === "queued" || next === "running") && attempts < 15) {
          window.setTimeout(() => {
            void poll();
          }, 1500);
        }
      } catch {
        /* ignore poll errors; refresh path handles hard failures */
      }
    };
    const t = window.setTimeout(() => {
      void poll();
    }, 1000);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [ticketId, workspaceId, summaryJob]);

  const summaryDisplay = useMemo(() => {
    if (aiSummary?.summary) return aiSummary;
    if (summaryJob?.status === "queued" || summaryJob?.status === "running") {
      return { summary: "Generating summary from user ticket messages..." };
    }
    return aiSummary;
  }, [aiSummary, summaryJob]);

  return { historicThreads, aiSummary: summaryDisplay, summaryJob, loading, error, refresh };
}
